import { ParticleRole, DEMON_PARTICLE_COUNT } from './types'

/**
 * Sample a position within a procedural humanoid silhouette.
 * The shape is built from overlapping ellipsoids/capsules — no model needed.
 * Y-up, centered at origin, ~6 units tall.
 */
function sampleHumanoidPosition(
  role: ParticleRole,
  rand: number,
): { x: number; y: number; z: number; size: number } {
  // Eyes: two tight clusters at head height
  if (role === ParticleRole.Eye) {
    const side = rand > 0.5 ? 1 : -1
    return {
      x: side * 0.3 + (Math.random() - 0.5) * 0.1,
      y: 2.5 + (Math.random() - 0.5) * 0.15,
      z: 0.4 + (Math.random() - 0.5) * 0.05,
      size: 3.0 + Math.random() * 2.0,
    }
  }

  const section = Math.random()
  let x: number, y: number, z: number, size: number

  if (section < 0.25) {
    // Head (sphere, r~0.5)
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)
    const r = Math.cbrt(Math.random()) * 0.5
    x = Math.sin(phi) * Math.cos(theta) * r
    y = 2.5 + Math.sin(phi) * Math.sin(theta) * r * 0.6
    z = Math.cos(phi) * r
    size = 2.0 + Math.random() * 1.5
  } else if (section < 0.6) {
    // Torso (ellipsoid, wider at shoulders)
    const t = Math.random() // 0=shoulders, 1=waist
    const widthScale = 1.0 - t * 0.3
    x = (Math.random() - 0.5) * 1.2 * widthScale
    y = 2.0 - t * 1.8
    z = (Math.random() - 0.5) * 0.6
    size = 2.5 + Math.random() * 2.0
  } else if (section < 0.8) {
    // Arms (extending from shoulders)
    const side = Math.random() > 0.5 ? 1 : -1
    const t = Math.random() // 0=shoulder, 1=hand
    x = side * (0.7 + t * 0.5) + (Math.random() - 0.5) * 0.2
    y = 1.8 - t * 1.0 + (Math.random() - 0.5) * 0.2
    z = (Math.random() - 0.5) * 0.2
    size = 1.5 + Math.random() * 1.0
  } else {
    // Legs (two cylinders)
    const side = Math.random() > 0.5 ? 1 : -1
    const t = Math.random()
    x = side * 0.3 + (Math.random() - 0.5) * 0.15
    y = 0.0 - t * 1.5 + (Math.random() - 0.5) * 0.15
    z = (Math.random() - 0.5) * 0.2
    size = 2.0 + Math.random() * 1.5
  }

  // Wisps extend further from body
  if (role === ParticleRole.Wisp) {
    const spread = 1.5 + Math.random() * 1.5
    x *= spread
    y += (Math.random() - 0.5) * 2.0
    z *= spread
    size *= 0.7
  }

  // Embers float upward and outward
  if (role === ParticleRole.Ember) {
    y += Math.random() * 1.5
    x += (Math.random() - 0.5) * 0.8
    size = 1.0 + Math.random() * 1.5
  }

  return { x, y, z, size }
}

/** Assign particle role by index */
function roleForIndex(i: number): ParticleRole {
  if (i < 600) return ParticleRole.Body   // 50%
  if (i < 900) return ParticleRole.Wisp   // 25%
  if (i < 1190) return ParticleRole.Ember // ~24%
  return ParticleRole.Eye                  // ~1%
}

export interface DemonParticleBuffers {
  positions: Float32Array
  offsets: Float32Array
  velocities: Float32Array
  roles: Float32Array
  randoms: Float32Array
  sizes: Float32Array
}

/** Build all per-particle attribute buffers for a single demon entity */
export function buildDemonParticles(): DemonParticleBuffers {
  const N = DEMON_PARTICLE_COUNT
  const positions = new Float32Array(N * 3)
  const offsets = new Float32Array(N * 3)
  const velocities = new Float32Array(N * 3)
  const roles = new Float32Array(N)
  const randoms = new Float32Array(N)
  const sizes = new Float32Array(N)

  for (let i = 0; i < N; i++) {
    const i3 = i * 3
    const rand = Math.random()
    randoms[i] = rand

    const role = roleForIndex(i)
    roles[i] = role

    const { x, y, z, size } = sampleHumanoidPosition(role, rand)
    offsets[i3] = x
    offsets[i3 + 1] = y
    offsets[i3 + 2] = z
    positions[i3] = x
    positions[i3 + 1] = y
    positions[i3 + 2] = z
    sizes[i] = size

    // Random velocity seeds for scatter/death animations
    velocities[i3] = (Math.random() - 0.5) * 2
    velocities[i3 + 1] = (Math.random() - 0.5) * 2
    velocities[i3 + 2] = (Math.random() - 0.5) * 2
  }

  return { positions, offsets, velocities, roles, randoms, sizes }
}
